import { useMemo, useState } from "react";
import { Search, Receipt as ReceiptIcon, Undo2, Minus, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { fmtDate, fmtNum, round2, uuid, vals } from "@/lib/format";
import { payKey } from "@/lib/tpv";
import { BigButton, Chip, Empty, PageTitle } from "@/components/ui-kit";
import Receipt from "@/components/Receipt";
import DataGrid from "@/components/DataGrid";
import useIsDesktop from "@/lib/useIsDesktop";
import { warrantyExpired, warrantyEnd } from "@/lib/vehicles";

function ReturnDialog({ sale, onClose }) {
  const { t, money, dispatch } = useApp();
  const [qty, setQty] = useState({});
  const [refund, setRefund] = useState(sale.payment === "credit" && sale.customer_id ? "credit" : "cash");
  const ratio = sale.subtotal ? sale.total / sale.subtotal : 1;
  const items = sale.items.map((i) => ({ ...i, rq: qty[i.product_id] || 0 })).filter((i) => i.rq > 0);
  const amount = round2(items.reduce((s, i) => s + i.rq * i.price * ratio, 0));
  const max = round2(sale.total - (sale.returned || 0));
  const save = () => {
    const data = { id: uuid(), sale_id: sale.id, receipt_no: sale.receipt_no, refund, amount,
      items: items.map((i) => ({ product_id: i.product_id, name: i.name, qty: i.rq, price: i.price })) };
    if (dispatch("sale.return", data)) { toast.success(t("return_saved")); onClose(); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="return-dialog">
        <DialogTitle className="font-heading text-2xl">{t("return_items")} #{sale.receipt_no}</DialogTitle>
        {sale.items.map((i) => {
          const q = qty[i.product_id] || 0;
          const set = (v) => setQty({ ...qty, [i.product_id]: Math.min(Math.max(v, 0), i.qty) });
          return (
            <div key={i.product_id} className="flex items-center gap-2" data-testid={`return-line-${i.product_id}`}>
              <div className="flex-1 min-w-0"><div className="font-bold truncate">{i.name}</div><div className="text-xs text-muted-foreground">{fmtNum(i.qty)} × {fmtNum(i.price)}</div>{i.warranty_months && <div className={`text-xs font-bold ${warrantyExpired(sale.created_at, i.warranty_months) ? "text-[#C7353B]" : "text-[#1F9D55]"}`} data-testid="return-warranty">{warrantyExpired(sale.created_at, i.warranty_months) ? t("warranty_expired") : t("warranty_ok")} · {warrantyEnd(sale.created_at, i.warranty_months).toISOString().slice(0, 10)}</div>}</div>
              <button type="button" onClick={() => set(q - 1)} className="w-11 h-11 rounded-lg bg-secondary grid place-items-center" data-testid="return-dec-btn"><Minus className="w-4 h-4" /></button>
              <span className="w-8 text-center font-bold" data-testid="return-qty">{q}</span>
              <button type="button" onClick={() => set(q + 1)} className="w-11 h-11 rounded-lg bg-secondary grid place-items-center" data-testid="return-inc-btn"><Plus className="w-4 h-4" /></button>
            </div>
          );
        })}
        <div className="flex gap-2">
          <Chip active={refund === "cash"} onClick={() => setRefund("cash")} data-testid="refund-cash">{t("refund_cash")}</Chip>
          {sale.customer_id && <Chip active={refund === "credit"} onClick={() => setRefund("credit")} data-testid="refund-credit">{t("refund_credit")}</Chip>}
        </div>
        <div className="flex justify-between font-bold text-lg"><span>{t("refund")}</span><span data-testid="return-amount">{money(amount)}</span></div>
        <BigButton disabled={!amount || amount > max + 0.01} onClick={save} className="w-full" data-testid="return-confirm-btn"><Check className="w-5 h-5" />{t("confirm")}</BigButton>
      </DialogContent>
    </Dialog>
  );
}

export default function SalesHistory() {
  const { view, t, lang, money, can } = useApp();
  const [days, setDays] = useState(7);
  const [q, setQ] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [ret, setRet] = useState(null);
  const isDesktop = useIsDesktop();
  const sales = useMemo(() => {
    const from = new Date(Date.now() - days * 86400000).toISOString();
    return vals(view.sales).filter((s) => s.created_at >= from && `${s.receipt_no} ${s.customer_name || ""}`.toLowerCase().includes(q.toLowerCase()))
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
  }, [view.sales, days, q]);
  return (
    <div>
      <PageTitle testid="sales-history-title">{t("sales_history")}</PageTitle>
      <div className="relative mb-3">
        <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-5 h-5 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("receipt_no")} data-testid="sales-search-input"
          className="w-full h-14 rounded-2xl border border-border bg-white ps-11 pe-3 focus:outline-none focus:ring-2 focus:ring-primary" />
      </div>
      <div className="flex gap-2 mb-4">{[1, 7, 30].map((d) => <Chip key={d} active={days === d} onClick={() => setDays(d)} data-testid={`sales-range-${d}`}>{t(`range_${d}`)}</Chip>)}</div>
      {!sales.length && !isDesktop && <Empty icon={ReceiptIcon} text={t("no_sales")} testid="sales-empty" />}
      {isDesktop && (
        <DataGrid testid="sales-grid" name="tickets" rows={days === 1 || days === 7 || days === 30 ? sales : sales} dateField="created_at" onRowClick={setReceipt}
          columns={[
            { key: "receipt_no", label: t("receipt_no") }, { key: "created_at", label: t("date"), get: (s) => fmtDate(s.created_at, lang) },
            { key: "payment", label: t("payment"), get: (s) => t(payKey(s.payment)) },
            { key: "customer_name", label: t("customer") }, { key: "staff_name", label: t("staff") },
            { key: "items", label: t("items"), align: "end", get: (s) => s.items.length },
            { key: "discount", label: t("discount"), align: "end", sum: true, get: (s) => s.discount || 0 },
            { key: "returned", label: t("returned"), align: "end", sum: true, get: (s) => s.returned || 0 },
            { key: "total", label: t("total"), align: "end", sum: true },
          ]}
          actions={(s) => (
            <span className="flex gap-1">
              <button type="button" onClick={() => setReceipt(s)} className="w-7 h-7 rounded hover:bg-[#DCE6EE] grid place-items-center" aria-label={t("receipt")} data-testid="sale-reprint-btn"><ReceiptIcon className="w-4 h-4" /></button>
              {can("returns") && s.total - (s.returned || 0) > 0 && <button type="button" onClick={() => setRet(s)} className="w-7 h-7 rounded hover:bg-[#DCE6EE] text-primary grid place-items-center" aria-label={t("return_items")} data-testid="sale-return-btn"><Undo2 className="w-4 h-4" /></button>}
            </span>
          )} />
      )}
      <div className={isDesktop ? "hidden" : "space-y-2"}>
        {sales.map((s) => (
          <div key={s.id} className="rounded-xl bg-white border border-border p-3 flex items-center gap-3 card-soft" data-testid="sale-history-row">
            <div className="flex-1 min-w-0">
              <div className="font-bold">#{s.receipt_no} · {money(s.total)} {s.returned > 0 && <span className="text-xs text-primary">({t("returned")} {money(s.returned)})</span>}</div>
              <div className="text-xs text-muted-foreground">{fmtDate(s.created_at, lang)} · {t(payKey(s.payment))} · {s.customer_name || s.staff_name}{s.pending && ` · ${t("pending")}`}</div>
            </div>
            <button type="button" onClick={() => setReceipt(s)} className="w-12 h-12 rounded-full border border-border grid place-items-center" aria-label={t("receipt")} data-testid="sale-reprint-btn"><ReceiptIcon className="w-5 h-5" /></button>
            {can("returns") && s.total - (s.returned || 0) > 0 && (
              <button type="button" onClick={() => setRet(s)} className="w-12 h-12 rounded-full border border-primary text-primary grid place-items-center" aria-label={t("return_items")} data-testid="sale-return-btn"><Undo2 className="w-5 h-5" /></button>
            )}
          </div>
        ))}
      </div>
      <Receipt sale={receipt} onClose={() => setReceipt(null)} />
      {ret && <ReturnDialog sale={ret} onClose={() => setRet(null)} />}
    </div>
  );
}
